<template>
  <div id="mmp-page-content-wrapper">
    <div class="mmp-main">
    <nav class="mmp-fixed-nav">
      <div class="mmp-user-info">
        <span class="mmp-user-avatar">
          <fa :icon="['fas', 'user']" />
        </span>
        <span class="mmp-user-greeting">
          <span class="mmp-user-greeting-label">Welcome back</span>
          <span class="mmp-user-greeting-name">{{ user ? user.name : '' }}</span>
        </span>
      </div>
      <div class="mmp-nav-actions">
        <button
          v-if="globalIsUserAllowed('Change_Password')"
          class="notif-bell-btn"
          :disabled="isTestAccount"
          :title="isTestAccount ? 'Cannot change password of test account' : 'Change Password'"
          @click="$bvModal.show('ChangePasswordModal')"
        >
          <fa :icon="['fas', 'key']" />
        </button>
        <button
          v-if="globalIsUserAllowed('Release_History')"
          class="notif-bell-btn"
          title="Release History"
          @click="$bvModal.show('HistoryModal')"
        >
          <fa :icon="['fas', 'history']" />
        </button>
        <!-- Temporarily only show notifications for GM -->
        <button
          class="notif-bell-btn" title="Notifications"
          v-if="globalIsUserAllowed('Dashboard_Notifications') && false"
          @click="notifPanelOpen = !notifPanelOpen"
          :class="{ 'notif-bell-btn--active': notifPanelOpen, 'notif-bell-btn--pulse': notifUnreadCount > 0 && !notifPanelOpen }"
        >
          <fa :icon="['fas', 'bell']" />
          <span v-if="notifUnreadCount > 0" class="notif-bell-badge">{{ notifUnreadCount > 99 ? '99+' : notifUnreadCount }}</span>
        </button>
        <button
          class="notif-bell-btn signout-btn"
          title="Sign Out"
          @click="signOut"
        >
          <fa :icon="['fas', 'sign-out-alt']" />
        </button>
      </div>
    </nav>

    <div class="container-fluid mmp-dashboard-content" :class="{ 'mmp-content--map-max': mapFullscreen }">
      <!-- <div class="row mmp-stats-row mt-4 no-gutters">
        <div class="col stat-card mr-3">
          <span class="stat-label">Role</span>
          <span class="stat-value">{{ user && user.role ? user.role : '—' }}</span>
        </div>
        <div class="col stat-card mr-3" v-if="user && user.group">
          <span class="stat-label">Group</span>
          <span class="stat-value">{{ user.group }}</span>
        </div>
        <div class="col stat-card mr-3">
          <span class="stat-label">Last Release</span>
          <span class="stat-value">{{ versionReleaseInfo ? versionReleaseInfo.version : 'N/A' }}</span>
        </div>
        <div class="col stat-card">
          <span class="stat-label">Release Date</span>
          <span class="stat-value">{{ formattedReleaseDate }}</span>
        </div>
      </div> -->

      <div class="row mmp-dashboard-section my-4 no-gutters">
        <div
          class="left-col pr-3"
          :class="{ 'left-col--scrollbar-visible': leftColHovered }"
          ref="leftCol"
          @mouseenter="leftColHovered = true"
          @mouseleave="leftColHovered = false"
        >
          <!-- OPTION 2: with more than one region, this is the same bar chart as
               MMP_Dashboard.vue (unchanged) — categories are regions, one series
               per roadway type. With exactly one region, that grouping doesn't
               apply, so instead render a single-series bar chart with categories
               being the roadway types themselves (one evenly-spaced bar each) —
               same chart type/axis treatment for visual consistency, just a
               different grouping. -->
          <div class="mmp-customer-select" v-if="globalIsUserAllowed('Dashboard_Customer_Dropdown')">
            <label class="mmp-customer-select-label" for="dashboardCustomerSelect">
              <fa :icon="['fas', 'eye']" class="mmp-customer-select-label-icon" />
              <span>Viewing</span>
            </label>
            <div class="mmp-customer-dropdown" v-click-outside="closeCustomerDropdown">
              <button
                id="dashboardCustomerSelect"
                type="button"
                class="mmp-customer-dropdown-toggle"
                :class="{ 'is-open': customerDropdownOpen }"
                :aria-expanded="customerDropdownOpen"
                aria-haspopup="listbox"
                @click="customerDropdownOpen = !customerDropdownOpen"
              >
                <span class="mmp-customer-dropdown-value">{{ currentCustomerName || 'Select customer' }}</span>
                <fa :icon="['fas', 'chevron-down']" class="mmp-customer-dropdown-caret" />
              </button>
              <transition name="mmp-dropdown-fade">
                <ul v-if="customerDropdownOpen" class="mmp-customer-dropdown-menu" role="listbox">
                  <li
                    v-for="org in dashboardOptions"
                    :key="org.id"
                    class="mmp-customer-dropdown-option"
                    :class="{ 'is-selected': org.id == selectedCustomerOrg }"
                    role="option"
                    :aria-selected="org.id == selectedCustomerOrg"
                    @click="selectCustomer(org.id)"
                  >
                    <span class="mmp-customer-dropdown-option-label">{{ org.company_name }}</span>
                    <fa v-if="org.id == selectedCustomerOrg" :icon="['fas', 'check']" class="mmp-customer-dropdown-check" />
                  </li>
                </ul>
              </transition>
            </div>
          </div>
          <div v-if="hasRoadTypeMileage" class="chart-card mb-3" :class="{ 'chart-card--compact': isSingleRegion }">
            <div class="chart-card-body d-flex flex-column">
              <div class="chart-header">
                <span class="chart-header-icon">
                  <fa :icon="['fas', 'chart-bar']"></fa>
                </span>
                <h4 class="chart-title">{{ currentCustomerName ? currentCustomerName + ' - ' : '' }}Mileage by Roadway Type</h4>
              </div>
              <div class="chart-container">
                <div v-if="chartLoading" class="chart-loading">
                  <span class="spinner-border spinner-border-md" role="status" aria-hidden="true"></span>
                </div>
                <template v-else>
                  <apexchart
                    v-if="!isSingleRegion"
                    :key="'multi-' + selectedCustomerOrg"
                    type="bar"
                    width="100%"
                    height="100%"
                    :options="chartOptions"
                    :series="series"
                  />
                  <apexchart
                    v-else
                    :key="'single-' + selectedCustomerOrg"
                    type="bar"
                    width="100%"
                    height="100%"
                    :options="singleRegionChartOptions"
                    :series="singleRegionSeries"
                  />
                </template>
              </div>
            </div>
          </div>
          <!-- OPTION 2: single region — merge Recent Releases and Total Mileage
               into one row instead of stacking a full-width table over a mileage
               strip that only has one (narrow) card in it. -->
          <div class="releases-card w-100 mb-3">
            <div class="releases-card-body d-flex flex-column">
              <div class="releases-header">
                <span class="releases-header-icon">
                  <fa :icon="['fas', 'tag']"></fa>
                </span>
                <h4 class="releases-title">{{ currentCustomerName ? currentCustomerName + ' - ' : '' }}Recent Releases</h4>
              </div>
              <vue-good-table
                class="releases-table"
                :rows="releaseRows"
                :columns="releaseColumns"
                styleClass="vgt-table modern-table"
              >
                <template #table-row="props">
                  <span v-if="props.column.field == 'release_date'">
                    {{ new Date(props.row.release_date).toLocaleDateString(language, { month: '2-digit', day: '2-digit', year: 'numeric' }) }}
                  </span>
                  <span v-else>
                    {{ props.row[props.column.field] }}
                  </span>
                </template>
              </vue-good-table>
            </div>
          </div>

          <div class="row layers-row no-gutters" id="mileage">
            <div class="col-12 total-mileage-strip">
              <div
                v-for="entry in totalMileage"
                :key="entry.region"
                class="total-mileage-card"
              >
                <div class="total-mileage-card-body">
                  <span class="total-mileage-region">{{ entry.region }}</span>
                  <div class="total-mileage-display">
                    <span class="total-mileage-value">{{ entry.mileage.toLocaleString() }}</span>
                    <span class="total-mileage-unit">mi</span>
                  </div>
                  <span class="total-mileage-label">{{ currentCustomerName ? currentCustomerName + ' - ' : '' }}Total Mileage</span>
                </div>
              </div>
            </div>
          </div>

        </div>

        <div class="right-col pl-3">
          <div class="map-card h-100 w-100" :class="{ 'map-card--fullscreen': mapFullscreen }">
            <div class="map-card-header d-flex justify-content-between align-items-center">
              <div class="map-card-title">
                <span class="map-card-title-icon">
                  <fa :icon="['fas', 'map']"></fa>
                </span>
                <span class="map-card-title-text">
                  {{ isUshrUser ? 'Map Overlays' : 'Map Overlays' }}
                </span>
              </div>
              <button
                class="map-fullscreen-btn"
                :title="mapFullscreen ? 'Minimize map' : 'Expand map'"
                :aria-label="mapFullscreen ? 'Minimize map' : 'Expand map'"
                @click="toggleMapFullscreen"
              >
                <fa :icon="['fas', mapFullscreen ? 'window-minimize' : 'expand']"></fa>
              </button>
            </div>
            <div class="map-body">
              <div class="h-100 w-100" id="mmp-dashboard-map"></div>
              <div class="map-layer-panel" :class="{ 'map-layer-panel--minimized': layerPanelMinimized }">
                <div class="map-layer-panel-header">
                  <h4 class="map-layer-panel-title">Layers</h4>
                  <button
                    class="map-layer-panel-minimize"
                    :aria-expanded="!layerPanelMinimized"
                    :title="layerPanelMinimized ? 'Expand layers' : 'Minimize layers'"
                    @click="layerPanelMinimized = !layerPanelMinimized"
                  >
                    <fa :icon="['fas', layerPanelMinimized ? 'chevron-up' : 'chevron-down']"></fa>
                  </button>
                </div>
                <template v-if="!layerPanelMinimized">
                  <draggable
                    v-model="layersButtonsCtrl"
                    tag="div"
                    class="layer-toggle-list"
                    handle=".layer-drag-handle"
                    @end.prevent="reorderLayers"
                  >
                    <div v-for="layer in layersButtonsCtrl" :key="layer.name" class="layer-row">
                      <fa :icon="['fas', 'grip-vertical']" class="layer-drag-handle" title="Drag to reorder"></fa>
                      <span class="layer-row-label">{{ layer.display_name }}</span>
                      <button
                        class="layer-toggle-switch"
                        :class="{ 'is-on': layer.visible }"
                        :aria-pressed="layer.visible"
                        :title="layer.visible ? 'Hide layer' : 'Show layer'"
                        @click="toggleLayer(layer.name)"
                      >
                        <span class="layer-toggle-knob"></span>
                      </button>
                    </div>
                  </draggable>
                  <p class="text-muted mb-0" v-if="!layersButtonsCtrl.length">
                    No layers loaded yet.
                  </p>
                </template>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div class="mmp-copyright">
      <a href="https://dmp-maps.com/legal/" class="mmp-attribution" target="_blank">
        © {{ new Date().getFullYear() }} Dynamic Map Platform North America, Inc. All Rights Reserved
      </a>
    </div>
    </div><!-- /.mmp-main -->
    <div class="notif-drawer" :class="{ 'notif-drawer--open': notifPanelOpen }" v-if="globalIsUserAllowed('Dashboard_Notifications') && false">
      <MMP_Notifications @close="notifPanelOpen = false" @unread-count="notifUnreadCount = $event" :user="user" />
    </div>
  </div>
</template>

<script>
import axios from 'axios'
import 'ol/ol.css'
import Attribution from 'ol/control/Attribution'
import Map from 'ol/Map'
import View from 'ol/View'
import { OSM } from 'ol/source'
import { Tile as TileLayer } from 'ol/layer'
import { get as projGet, transform } from 'ol/proj'
import { createLayers, orderMapLayers } from '../../utils/utils.js'
import draggable from 'vuedraggable'
import { mapGetters } from 'vuex'
import MMP_Notifications from './MMP_Notifications.vue'

// Placeholder data until a real mileage-by-roadway-type endpoint exists for MMP
function randomMileageSeries() {
  return Array.from({ length: 4 }, () => Math.floor(Math.random() * 4000) + 1000)
}

// Placeholder data until a real per-region release date endpoint exists for MMP
function randomRecentDate(maxDaysAgo = 60) {
  const date = new Date()
  date.setDate(date.getDate() - Math.floor(Math.random() * maxDaysAgo))
  return date
}

// OPTION 2: fixed per-bar colors for the single-region chart (Motorway, Trunk,
// Primary, Secondary, in that order) — same palette the multi-region chart's
// legend uses, applied via plotOptions.bar.distributed instead of a legend.
const ROAD_TYPE_COLORS = ['#2e93fA', '#1f9d55', '#ffa726', '#ef5350']

export default {
  name: 'MMP_Dashboard',
  components: {
    draggable,
    MMP_Notifications
  },
  directives: {
    'click-outside': {
      bind(el, binding) {
        el.clickOutsideEvent = event => {
          if (el !== event.target && !el.contains(event.target)) {
            binding.value(event)
          }
        }
        document.addEventListener('click', el.clickOutsideEvent)
      },
      unbind(el) {
        document.removeEventListener('click', el.clickOutsideEvent)
      }
    }
  },
  props: {
    user: { type: Object, default: null },
    isUshrUser: { type: Boolean, default: false },
    versionReleaseInfo: { type: Object, default: null }
  },
  data() {
    return {
      notifPanelOpen: false,
      notifUnreadCount: 0,
      layerPanelMinimized: false,
      mapFullscreen: false,
      map: null,
      resizeObserver: null,
      layersLoadStarted: false,
      language: navigator.language,
      layersObj: {},
      hasRoadTypeMileage: null,
      chartLoading: true,
      leftColHovered: false,
      totalMileage: [],
      // Orgs the dashboard knows how to fetch stats for — mirrors the switch
      // in getDashboardStats(). Only shown/usable behind the
      // Dashboard_Customer_Dropdown access control module.
      dashboardOptions: [],
      selectedCustomerOrg: null,
      customerDropdownOpen: false,
      // OPTION 2: single-region chart — categories are roadway types instead of
      // regions, one bar each via plotOptions.bar.distributed (see chartOptions
      // below for the multi-region shape this mirrors).
      singleRegionSeries: [{ name: 'Mileage', data: [] }],
      singleRegionChartOptions: {
        chart: {
          type: 'bar',
          toolbar: { show: false }
        },
        plotOptions: {
          bar: {
            horizontal: false,
            columnWidth: '45%',
            distributed: true,
            endingShape: 'rounded'
          }
        },
        colors: ROAD_TYPE_COLORS,
        dataLabels: {
          enabled: false
        },
        stroke: {
          show: true,
          width: 2,
          colors: ['transparent']
        },
        xaxis: {
          categories: ['Motorway', 'Trunk', 'Primary', 'Secondary'],
          title: { text: 'Roadway Type' }
        },
        yaxis: {
          title: { text: 'Miles' },
          labels: {
            formatter: val => {
              const orig = Math.pow(val, 3)
              if (orig >= 1e9) return (orig / 1e9).toFixed(1) + 'B'
              if (orig >= 1e6) return (orig / 1e6).toFixed(1) + 'M'
              if (orig >= 1e3) return (orig / 1e3).toFixed(1) + 'K'
              return Math.floor(orig).toString()
            }
          }
        },
        fill: {
          opacity: 1
        },
        legend: {
          show: false
        },
        tooltip: {
          y: {
            formatter: val => {
              const orig = Math.pow(val, 3)
              if (orig >= 1e9) return `${(orig / 1e9).toFixed(2)}B mi`
              if (orig >= 1e6) return `${(orig / 1e6).toFixed(2)}M mi`
              if (orig >= 1e3) return `${(orig / 1e3).toFixed(2)}K mi`
              return `${Math.floor(orig).toLocaleString()} mi`
            }
          }
        }
      },
      layersButtonsCtrl: [],
      releaseColumns: [
        { label: 'Region', field: 'region', sortable: true },
        { label: 'Release Date', field: 'release_date', sortable: true }
      ],
      releaseRows: [
        { region: 'North America', release_date: randomRecentDate() },
        { region: 'Europe', release_date: randomRecentDate() },
        { region: 'Japan', release_date: randomRecentDate() },
        { region: 'Middle East', release_date: randomRecentDate() }
      ],
      series: [
        { name: 'Motorway', data: randomMileageSeries() },
        { name: 'Trunk', data: randomMileageSeries() },
        { name: 'Primary', data: randomMileageSeries() },
        { name: 'Secondary', data: randomMileageSeries() }
      ],
      chartOptions: {
        chart: {
          type: 'bar',
          toolbar: { show: false }
        },
        plotOptions: {
          bar: {
            horizontal: false,
            columnWidth: '55%',
            endingShape: 'rounded'
          }
        },
        dataLabels: {
          enabled: false
        },
        stroke: {
          show: true,
          width: 2,
          colors: ['transparent']
        },
        xaxis: {
          categories: [],
          title: { text: 'Region' }
        },
        yaxis: {
          title: { text: 'Miles' },
          labels: {
            formatter: val => {
              const orig = Math.pow(val, 3)
              if (orig >= 1e9) return (orig / 1e9).toFixed(1) + 'B'
              if (orig >= 1e6) return (orig / 1e6).toFixed(1) + 'M'
              if (orig >= 1e3) return (orig / 1e3).toFixed(1) + 'K'
              return Math.floor(orig).toString()
            }
          }
        },
        fill: {
          opacity: 1
        },
        legend: {
          position: 'top'
        },
        tooltip: {
          y: {
            formatter: val => {
              const orig = Math.pow(val, 3)
              if (orig >= 1e9) return `${(orig / 1e9).toFixed(2)}B mi`
              if (orig >= 1e6) return `${(orig / 1e6).toFixed(2)}M mi`
              if (orig >= 1e3) return `${(orig / 1e3).toFixed(2)}K mi`
              return `${Math.floor(orig).toLocaleString()} mi`
            }
          }
        }
      }
    }
  },
  computed: {
    ...mapGetters(['globalIsUserAllowed']),
    currentCustomerName() {
      const match = this.dashboardOptions.find(org => org.id == this.selectedCustomerOrg)
      return match ? match.company_name : ''
    },
    formattedReleaseDate() {
      if (!this.versionReleaseInfo || !this.versionReleaseInfo.release_date) return 'N/A'
      return new Date(this.versionReleaseInfo.release_date).toLocaleDateString(this.language, {
        month: '2-digit',
        day: '2-digit',
        year: 'numeric'
      })
    },
    isTestAccount() {
      return this.user && this.user.test
    },
    // OPTION 2: drives both the compact card height and the chart/bar-list swap.
    isSingleRegion() {
      return this.chartOptions.xaxis.categories.length === 1
    }
  },
  watch: {
    user() {
      this.loadLayers()
      this.getDashboardStats();
    },
    isUshrUser() {
      this.loadLayers()
    }
  },
  mounted() {
    this.$nextTick(() => {
      this.initDashboardMap()
      this.getDashboardOptions()
    })
    window.addEventListener('keydown', this.handleMapFullscreenKey)
    // Safety net for a known Chromium/WebKit quirk where :hover-driven
    // scrollbar styling can get stuck visible if the mouse leaves .left-col
    // while dragging its scrollbar thumb — the browser's hover hit-test for
    // the underlying element doesn't always re-run once the drag ends
    // outside it. Double-check on every mouseup and correct it if needed.
    window.addEventListener('mouseup', this.recheckLeftColHover)
  },
  beforeDestroy() {
    window.removeEventListener('keydown', this.handleMapFullscreenKey)
    window.removeEventListener('mouseup', this.recheckLeftColHover)
    if (this.resizeObserver) {
      this.resizeObserver.disconnect()
      this.resizeObserver = null
    }
    if (this.map) {
      this.map.setTarget(null)
      this.map = null
    }
  },
  methods: {
    selectCustomer(id) {
      this.customerDropdownOpen = false
      if (this.selectedCustomerOrg === id) return
      this.selectedCustomerOrg = id
      this.getDashboardStats()
    },
    closeCustomerDropdown() {
      this.customerDropdownOpen = false
    },
    recheckLeftColHover(event) {
      if (!this.leftColHovered || !this.$refs.leftCol) return
      const rect = this.$refs.leftCol.getBoundingClientRect()
      const inside =
        event.clientX >= rect.left &&
        event.clientX <= rect.right &&
        event.clientY >= rect.top &&
        event.clientY <= rect.bottom
      if (!inside) this.leftColHovered = false
    },
    signOut() {
      sessionStorage.removeItem('token')
      sessionStorage.removeItem('pid')
      this.$emit('clearCurrentPositionsInterval')
      this.$router.push({ name: 'Login' })
    },
    initDashboardMap() {
      this.map = new Map({
        controls: [new Attribution({ collapsible: true })],
        target: 'mmp-dashboard-map',
        pixelRatio: 1,
        layers: [
          new TileLayer({
            source: new OSM(),
            name: 'OSM base layer',
            zIndex: 2
          })
        ],
        view: new View({
          center: [0, 0],
          zoom: 3,
          minZoom: 2,
          extent: projGet('EPSG:3857').getExtent(),
          constrainOnlyCenter: false,
          multiWorld: false
        })
      })

      const mapEl = document.getElementById('mmp-dashboard-map')
      this.resizeObserver = new ResizeObserver(() => {
        this.map?.updateSize()
      })
      this.resizeObserver.observe(mapEl)

      this.getDashboardStats()

      this.fitToDefaultExtent()

      this.loadLayers()
    },
    // user/isUshrUser arrive asynchronously from the parent's getUser() call, which
    // may still be in flight when this component mounts — wait for real data before
    // deciding which layers to load instead of firing on the placeholder user object.
    loadLayers() {
      if (this.layersLoadStarted) return
      if (!this.map) return
      if (!this.user || this.user.id == null) return

      this.layersLoadStarted = true
      if (this.isUshrUser) {
        this.loadVehiclePositions()
      } else {
        this.loadDeliveryCoverage()
      }
    },
    getDashboardStats() {
      let endpoint = '';
      switch (this.selectedCustomerOrg || this.user.org) {
        case '2':
          endpoint = 'getGMDashboardStats';
          break;
        case '4':
          endpoint = 'getNissanDashboardStats';
          break;
        case '0':
          endpoint = 'getSubaruDashboardStats';
          break;
        case '3':
          endpoint = 'getRivianDashboardStats';
          break;
        default:
          endpoint = 'getGMDashboardStats';
          break;
      }
      if (sessionStorage.token) {
        // Gate the apexchart's mount behind a spinner while stats are in
        // flight — mounting it fresh (see the apexchart :key above) into an
        // already-stable, already-visible slot avoids ApexCharts measuring
        // its container mid-layout and locking in a too-short height that
        // .chart-card's overflow:hidden then clips permanently.
        this.chartLoading = true
        axios.get(process.env.API_LOCATION + `/api/drt/${endpoint}`, {
          headers: {
            'x-auth-token': sessionStorage.token
          },
        })
        .then(response => {
          let content = response.data;

          this.regions = content.map(d => ({
            name: this.mapRegionName(d.region),
            count: Math.floor(d.total_mileage)  // Remove decimals
          }));

          this.chartOptions = {
            ...this.chartOptions,
            xaxis: {
              categories: content.map(d => this.mapRegionName(d.region)),
              title: { text: 'Region' }
            }
          }

          this.releaseRows = content.map(d => ({
            region: this.mapRegionName(d.region),
            release_date: d.release_date
          }))

          const types = ['motorway', 'trunk', 'primary', 'secondary'];
          this.totalMileage = content.map(d => ({
            region: this.mapRegionName(d.region),
            mileage: Math.round(parseFloat(d.total_mileage) || 0)
          }));

          this.hasRoadTypeMileage = content.some(d =>
            types.some(t => d[`${t}_mileage`] != null)
          );

          if (this.hasRoadTypeMileage) {
            this.series = types.map(type => ({
              name: this.capitalizeFirstLetter(type),
              data: content.map(d => Math.cbrt(d[`${type}_mileage`] || 0))
            }));

            // OPTION 2: single region — same cube-root treatment as the
            // multi-region series, just one bar per roadway type instead of
            // one bar per region.
            if (content.length === 1) {
              this.singleRegionSeries = [{
                name: 'Mileage',
                data: types.map(type => Math.cbrt(content[0][`${type}_mileage`] || 0))
              }]
            }
          }

          // Let this render pass land first — it updates releaseRows/
          // totalMileage/etc, and .releases-card's height shifts with the
          // new row count. chart-card sits earlier in the template, so if
          // chartLoading flipped in the same pass, the fresh apexchart would
          // mount and measure its container before releases-card's new
          // height is actually in the DOM, locking in the wrong size.
          // Waiting a tick lets that layout settle first.
          this.$nextTick(() => {
            this.chartLoading = false
          })
        })
        .catch(error => {
          console.log(error)
          this.chartLoading = false
        })
      } else {
        console.log('No User Token')
      }
    },
    async fitToDefaultExtent() {
      if (!sessionStorage.token) return
      try {
        const response = await axios.get(process.env.API_LOCATION + '/api/users/getAllExtents', {
          headers: { 'x-auth-token': sessionStorage.token }
        })
        const extents = response.data
        const match = extents.find(e => e.extent_description == 'North America') || extents[0]
        if (!match) return

        const extentCenter = match.extent_center.split(',').map(Number)

        const coordCenter = transform(extentCenter, projGet('EPSG:4326'), projGet('EPSG:3857'))

        this.map.setView(new View({
          center: coordCenter,
          zoom: 3,
          minZoom: 2,
          // Constrain the full viewport (not just the center) to the valid Web
          // Mercator world bounds. Constraining to the customer's own (much
          // smaller) extent with constrainOnlyCenter:true let the viewport
          // edges scroll past real map coverage once zoomed out toward
          // minZoom, revealing blank space above/below the tiles.
          extent: projGet('EPSG:3857').getExtent(),
          constrainOnlyCenter: false,
          multiWorld: false
        }))
      } catch (error) {
        console.log(error)
      }
    },
    async loadVehiclePositions() {
      if (!sessionStorage.token) return
      try {
        const response = await axios.get(process.env.API_LOCATION + '/api/layer/release', {
          headers: { 'x-auth-token': sessionStorage.token },
          params: { release_id: 25 } // Current Positions + Trips release
        })
        const content = response.data
        if (content.errors) {
          console.log('release API Call Error: ' + content.errors)
          return
        }
        const layerLinks = content
          .filter(e => {
            if (e.visibility == false) return false
            // USHR users: only show layers explicitly flagged for the USHR dashboard.
            if (this.isUshrUser) return e.link_object?.info?.showOnUshrDashboard == true
            // Everyone else: exclude customer-specific layers (customer_id null/absent).
            return e.link_object?.info?.customer_id == null
          })
          .map(link => {
            let linkObj = link.link_object
            linkObj.display_name = linkObj.name.replace(/_/g, ' ')
            return linkObj
          })
        const defaultOnLayers = ['Trips', 'Current Positions']
        const layersObj = createLayers(layerLinks, this.map)
        layerLinks.forEach(link => {
          const entry = layersObj[link.name]
          if (entry) {
            const shouldBeVisible = defaultOnLayers.includes(link.name)
            const isOnMap = this.map.getLayers().getArray().includes(entry.layer)
            // createLayers() already adds the layer to the map when zoomControl is falsy —
            // reconcile that against the intended default-on set (Trips + Current Positions
            // only) instead of trusting it blindly.
            if (shouldBeVisible && !isOnMap) {
              this.map.addLayer(entry.layer)
            } else if (!shouldBeVisible && isOnMap) {
              this.map.removeLayer(entry.layer)
            }
            this.layersObj[link.name] = entry
            this.layersButtonsCtrl.push({ name: link.name, display_name: link.display_name, visible: shouldBeVisible,  })
          }
        })
      } catch (error) {
        console.log(error)
      }
    },
    async loadDeliveryCoverage() {
      if (!sessionStorage.token || !this.user) return
      const isGenericUser = this.user.role == 'Generic User'
      const endpoint = isGenericUser ? 'getGenericCoverageLayers' : 'getCustomerCoverageLayers'
      try {
        const response = await axios.get(process.env.API_LOCATION + '/api/layer/' + endpoint, {
          headers: { 'x-auth-token': sessionStorage.token },
          params: { customer_id: this.user.org }
        })
        const content = response.data
        if (content.errors) {
          console.log(endpoint + ' API Call Error: ' + content.errors)
          return
        }
        const layerLinks = content.rows
          .filter(e => e.visibility != false)
          .map(link => {
            let linkObj = link.link_object
            linkObj.display_name = linkObj.name.replace(/_/g, ' ')
            return linkObj
          })
        this.layersObj = createLayers(layerLinks, this.map)
        this.layersButtonsCtrl = layerLinks.map(l => ({
          name: l.name,
          display_name: l.display_name,
          visible: !l.zoomControl
        }))
      } catch (error) {
        console.log(error)
      }
    },
    toggleLayer(layerName) {
      const entry = this.layersButtonsCtrl.find(l => l.name === layerName)
      const layerEntry = this.layersObj[layerName]
      if (!entry || !layerEntry) return
      if (entry.visible) {
        this.map.removeLayer(layerEntry.layer)
      } else {
        this.map.addLayer(layerEntry.layer)
      }
      entry.visible = !entry.visible
    },
    reorderLayers() {
      if (!this.map || !this.layersButtonsCtrl.length) return
      orderMapLayers(this.layersObj, this.layersButtonsCtrl, this.map)
    },
    toggleMapFullscreen() {
      this.mapFullscreen = !this.mapFullscreen
      // The ResizeObserver on the map element handles updateSize once the card
      // resizes; nudge it on the next frame in case the observer misses the jump.
      this.$nextTick(() => {
        setTimeout(() => this.map?.updateSize(), 60)
      })
    },
    handleMapFullscreenKey(e) {
      if (e.key === 'Escape' && this.mapFullscreen) {
        this.toggleMapFullscreen()
      }
    },
    mapRegionName(code) {
      switch(code) {
        case 'NA': return 'North America';
        case 'EU': return 'Europe';
        case 'JP': return 'Japan';
        case 'ME': return 'Middle East';
        default: return code;
      }
    },
    capitalizeFirstLetter(str) {
      return str.charAt(0).toUpperCase() + str.slice(1);
    },
    getDashboardOptions() {
      if (sessionStorage.token) {
        axios.get(
          `${process.env.API_LOCATION}/api/company/dashboardOrgs`,
          { headers: { 'x-auth-token': sessionStorage.token } }
        ).then(response => {
          const content = response.data
          if (content.errors) {
            console.log('dashboardOrgs API CALL ERROR: ' + content.errors)
          } else {
            this.dashboardOptions = content
            const matchedOrg = this.dashboardOptions.find((org) => org.id == this.user?.org)
            const defaultOrg = this.dashboardOptions.find((org) => org.id == 2)
            this.selectedCustomerOrg = (matchedOrg || defaultOrg)?.id
          }
        })
        .catch(function (error) {
          console.log(error)
        })
      }
    }
  }
}
</script>

<style scoped>
#mmp-page-content-wrapper {
  display: flex;
  flex-direction: row;
  height: 100%;
  min-height: 0;
  overflow: hidden;
  background-color: #f8f9fa;
}

.mmp-main {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.notif-drawer {
  flex-shrink: 0;
  width: 0;
  overflow: hidden;
  background: #fff;
  border-left: 1px solid #dee2e6;
  transition: width 0.28s cubic-bezier(0.4, 0, 0.2, 1);
}

.notif-drawer--open {
  width: 340px;
}

.mmp-nav-actions {
  display: flex;
  align-items: center;
  gap: 10px;
}

.notif-bell-btn {
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 42px;
  height: 42px;
  border: 1px solid rgba(6, 60, 109, 0.1);
  border-radius: 12px;
  background: rgba(255, 255, 255, 0.6);
  color: #4A74A8;
  font-size: 1.05rem;
  cursor: pointer;
  transition: background 0.18s ease, color 0.18s ease, box-shadow 0.18s ease, transform 0.18s ease;
  flex: 0 0 auto;
}

.signout-btn:hover {
  background: #fff;
  color: #dc3545;
  box-shadow: 0 4px 14px rgba(220, 53, 69, 0.18);
}

.notif-bell-btn:hover {
  background: #ffffff;
  color: #063c6d;
  box-shadow: 0 4px 14px rgba(6, 60, 109, 0.14);
  transform: translateY(-1px);
}

.notif-bell-btn:disabled {
  opacity: 0.4;
  cursor: not-allowed;
}

.notif-bell-btn:disabled:hover {
  background: rgba(255, 255, 255, 0.6);
  color: #4A74A8;
  box-shadow: none;
  transform: none;
}

.notif-bell-btn--active {
  background: linear-gradient(135deg, #0a548f 0%, #063c6d 100%);
  color: #ffffff;
  border-color: transparent;
  box-shadow: 0 4px 14px rgba(6, 60, 109, 0.28);
}

.notif-bell-badge {
  position: absolute;
  top: -5px;
  right: -5px;
  min-width: 18px;
  height: 18px;
  padding: 0 4px;
  border-radius: 9px;
  background: #dc3545;
  color: #fff;
  font-size: 0.65rem;
  font-weight: 700;
  line-height: 18px;
  text-align: center;
  pointer-events: none;
}

.notif-bell-btn--pulse {
  animation: bellPulse 1.6s infinite;
}

@keyframes bellPulse {
  0%   { box-shadow: 0 0 0 0 rgba(220, 53, 69, 0.6); }
  70%  { box-shadow: 0 0 0 10px rgba(220, 53, 69, 0); }
  100% { box-shadow: 0 0 0 0 rgba(220, 53, 69, 0); }
}

.mmp-fixed-nav {
  flex: 0 0 auto;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  padding: 0.65rem 24px;
  background: rgba(255, 255, 255, 0.75);
  backdrop-filter: blur(16px) saturate(180%);
  -webkit-backdrop-filter: blur(16px) saturate(180%);
  border-bottom: 1px solid rgba(6, 60, 109, 0.08);
  box-shadow: 0 1px 12px rgba(6, 60, 109, 0.06);
}

.container-fluid.mmp-dashboard-content {
  position: relative; /* positioning context for the maximized map */
  display: flex;
  flex-direction: column;
  flex: 1 1 auto;
  min-height: 0;
  overflow-y: auto;
  padding: 0px 20px 20px 20px;
}

/* When the map is maximized, lock the page scroll so the map stays put */
.container-fluid.mmp-dashboard-content.mmp-content--map-max {
  overflow: hidden;
}

.mmp-bottom-spacer {
  flex: 0 0 auto;
  height: 1.5rem;
  width: 100%;
}

.mmp-user-info {
  display: flex;
  align-items: center;
  flex-direction: row;
  color: #063c6d;
  gap: 12px;
}

.mmp-user-avatar {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: linear-gradient(135deg, #0a548f 0%, #063c6d 100%);
  box-shadow: 0 2px 8px rgba(6, 60, 109, 0.25);
  overflow: hidden;
  flex: 0 0 auto;
}

.mmp-user-avatar svg {
  width: 18px;
  height: 18px;
  color: #ffffff;
}

.mmp-user-greeting {
  display: flex;
  flex-direction: column;
  line-height: 1.2;
}

.mmp-user-greeting-label {
  font-size: 0.72rem;
  font-weight: 500;
  color: #8ab0cc;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.mmp-user-greeting-name {
  font-size: 1.02rem;
  font-weight: 700;
  color: #063c6d;
}

.mmp-customer-select {
  display: flex;
  align-items: center;
  justify-content: flex-start;
  gap: 8px;
  max-width: 50%;
  margin-bottom: 0.6rem;
}

.mmp-customer-select-label {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  margin: 0;
  padding: 4px 12px;
  font-size: 0.68rem;
  font-weight: 700;
  color: #0a548f;
  text-transform: uppercase;
  letter-spacing: 0.8px;
  white-space: nowrap;
  background: linear-gradient(135deg, rgba(10, 84, 143, 0.1) 0%, rgba(6, 60, 109, 0.06) 100%);
  border: 1px solid rgba(6, 60, 109, 0.12);
  border-radius: 999px;
  box-shadow: 0 1px 3px rgba(6, 60, 109, 0.06);
}

.mmp-customer-select-label-icon {
  font-size: 0.72rem;
  color: rgba(10, 84, 143, 0.75);
}

.mmp-customer-dropdown {
  position: relative;
  min-width: 200px;
}

.mmp-customer-dropdown-toggle {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
  width: 100%;
  height: 36px;
  padding: 0 0.9rem;
  font-size: 0.82rem;
  font-weight: 600;
  color: #063c6d;
  background-color: #ffffff;
  border: 1px solid rgba(6, 60, 109, 0.18);
  border-radius: 999px;
  box-shadow: 0 1px 3px rgba(6, 60, 109, 0.06);
  cursor: pointer;
  transition: border-color 0.15s ease, box-shadow 0.15s ease;
}

.mmp-customer-dropdown-toggle:hover {
  border-color: rgba(6, 60, 109, 0.35);
  box-shadow: 0 2px 8px rgba(6, 60, 109, 0.1);
}

.mmp-customer-dropdown-toggle.is-open {
  border-color: #0a548f;
  box-shadow: 0 0 0 3px rgba(10, 84, 143, 0.15);
}

.mmp-customer-dropdown-value {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.mmp-customer-dropdown-caret {
  flex: 0 0 auto;
  font-size: 0.7rem;
  color: #0a548f;
  transition: transform 0.2s ease;
}

.mmp-customer-dropdown-toggle.is-open .mmp-customer-dropdown-caret {
  transform: rotate(180deg);
}

.mmp-customer-dropdown-menu {
  position: absolute;
  top: calc(100% + 6px);
  left: 0;
  z-index: 20;
  width: 100%;
  max-height: 260px;
  overflow-y: auto;
  margin: 0;
  padding: 6px;
  list-style: none;
  background: #ffffff;
  border: 1px solid rgba(6, 60, 109, 0.1);
  border-radius: 12px;
  box-shadow: 0 10px 30px rgba(6, 60, 109, 0.16), 0 2px 6px rgba(6, 60, 109, 0.08);
}

.mmp-customer-dropdown-option {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.5rem;
  padding: 8px 10px;
  font-size: 0.82rem;
  font-weight: 500;
  color: #063c6d;
  border-radius: 8px;
  cursor: pointer;
  transition: background-color 0.12s ease, color 0.12s ease;
}

.mmp-customer-dropdown-option:hover {
  background-color: rgba(10, 84, 143, 0.08);
}

.mmp-customer-dropdown-option.is-selected {
  background-color: rgba(10, 84, 143, 0.12);
  font-weight: 700;
  color: #0a548f;
}

.mmp-customer-dropdown-option-label {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.mmp-customer-dropdown-check {
  flex: 0 0 auto;
  font-size: 0.72rem;
  color: #0a548f;
}

.mmp-dropdown-fade-enter-active,
.mmp-dropdown-fade-leave-active {
  transition: opacity 0.15s ease, transform 0.15s ease;
}

.mmp-dropdown-fade-enter,
.mmp-dropdown-fade-leave-to {
  opacity: 0;
  transform: translateY(-6px);
}

.mmp-dashboard-section {
  flex: 1 1 auto;
  min-height: 0;
  display: flex;
  flex-wrap: nowrap;
  width: 100%;
  overflow: hidden;
}

.left-col,
.right-col {
  display: flex;
  flex-direction: column;
  flex: 1 1 50%;
  max-width: 50%;
  min-height: 0;
  min-width: 0;
}

/* The row itself no longer scrolls (see .mmp-dashboard-section above) — only
   the left column's own content scrolls internally when it's taller than
   the available height. This keeps the map (right-col) pinned to the row's
   full height at all times instead of staying at its pre-overflow size
   while the left column's content spills out below it. */
.left-col {
  overflow-y: auto;
  overflow-x: hidden;
}
.left-col::-webkit-scrollbar {
  width: 5px;
  background-color: transparent !important;
}
.left-col::-webkit-scrollbar-track {
  background-color: transparent !important;
  box-shadow: none !important;
}
.left-col::-webkit-scrollbar-thumb {
  background-color: transparent !important;
  border-radius: 999px;
}
.left-col--scrollbar-visible::-webkit-scrollbar-track {
  background-color: rgba(6, 60, 109, 0.06) !important;
  border-radius: 999px;
}
.left-col--scrollbar-visible::-webkit-scrollbar-thumb {
  background-color: rgba(6, 60, 109, 0.25) !important;
}
.left-col--scrollbar-visible::-webkit-scrollbar-thumb:hover {
  background-color: rgba(6, 60, 109, 0.5) !important;
}

.chart-card {
  flex: 1 1 auto;
  min-height: 260px;
  background: linear-gradient(135deg, #ffffff 0%, #f6fafd 100%);
  border: 1px solid rgba(6, 60, 109, 0.08);
  border-radius: 14px;
  box-shadow: 0 2px 10px rgba(6, 60, 109, 0.07);
  overflow: hidden;
  display: flex;
  flex-direction: column;
  transition: min-height 0.2s ease;
}

/* OPTION 2: single-region case — same height as the multi-region chart card. */
.chart-card--compact {
  flex: 1 1 auto;
  min-height: 260px;
}

.chart-card-body {
  flex: 1 1 auto;
  min-height: 0;
  padding: 1.1rem 1.3rem;
}

.chart-header {
  display: flex;
  align-items: center;
  gap: 0.6rem;
  margin-bottom: 0.9rem;
}

.chart-header-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 30px;
  height: 30px;
  border-radius: 9px;
  background-color: rgba(6, 60, 109, 0.1);
  color: #063c6d;
  font-size: 0.85rem;
}

.chart-title {
  margin: 0;
  font-size: 1rem;
  font-weight: 700;
  color: #063c6d;
  letter-spacing: 0.2px;
}

.total-mileage-strip {
  display: flex;
  flex-wrap: wrap;
  gap: 0.75rem;
}

.total-mileage-card {
  flex: 1 1 0;
  min-width: 150px;
  background: linear-gradient(135deg, #ffffff 0%, #f4f8fb 100%);
  border: 1px solid rgba(6, 60, 109, 0.08);
  border-radius: 12px;
  box-shadow: 0 1px 3px rgba(6, 60, 109, 0.08);
  transition: transform 0.15s ease, box-shadow 0.15s ease;
}

.total-mileage-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 6px 16px rgba(6, 60, 109, 0.14);
}

.total-mileage-card-body {
  display: flex;
  flex-direction: column;
  padding: 0.85rem 1.1rem;
}

.total-mileage-region {
  font-size: 0.7rem;
  color: #8ab0cc;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  font-weight: 600;
  margin-bottom: 0.35rem;
}

.total-mileage-display {
  display: flex;
  align-items: baseline;
  gap: 0.35rem;
}

.total-mileage-value {
  font-size: 1.9rem;
  font-weight: 700;
  color: #063c6d;
  line-height: 1;
}

.total-mileage-unit {
  font-size: 0.9rem;
  color: #8ab0cc;
  font-weight: 500;
}

.total-mileage-label {
  font-size: 0.72rem;
  color: #9fb4c4;
  margin-top: 0.35rem;
}

.releases-card {
  flex: 0 0 auto;
  min-height: 0;
  background: linear-gradient(135deg, #ffffff 0%, #f6fafd 100%);
  border: 1px solid rgba(6, 60, 109, 0.08);
  border-radius: 14px;
  box-shadow: 0 2px 10px rgba(6, 60, 109, 0.07);
  overflow: hidden;
}

.releases-card-body {
  min-height: 0;
  padding: 1.1rem 1.3rem;
}

.releases-header {
  display: flex;
  align-items: center;
  gap: 0.6rem;
  margin-bottom: 0.9rem;
}

.releases-header-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 30px;
  height: 30px;
  border-radius: 9px;
  background-color: rgba(6, 60, 109, 0.1);
  color: #063c6d;
  font-size: 0.85rem;
}

.releases-title {
  margin: 0;
  font-size: 1rem;
  font-weight: 700;
  color: #063c6d;
  letter-spacing: 0.2px;
}

.chart-container {
  flex: 1 1 auto;
  min-height: 0;
  height: auto;
}

.chart-loading {
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100%;
  min-height: 200px;
  color: rgba(6, 60, 109, 0.55);
}

.releases-table {
  flex: 0 0 auto;
}

.layers-row {
  display: flex;
  flex-wrap: nowrap;
  flex: 0 0 auto;
  width: 100%;
}

.releases-table ::v-deep(.vgt-table.modern-table) {
  border: none;
  font-size: 0.85rem;
  background: transparent;
}

.releases-table ::v-deep(.vgt-table.modern-table th) {
  background-color: transparent;
  color: #8ab0cc;
  border: none;
  border-bottom: 1px solid rgba(6, 60, 109, 0.12);
  font-weight: 600;
  font-size: 0.72rem;
  text-transform: uppercase;
  letter-spacing: 0.6px;
  white-space: nowrap;
  padding: 8px 12px;
}

.releases-table ::v-deep(.vgt-table.modern-table td) {
  border: none;
  border-bottom: 1px solid rgba(6, 60, 109, 0.06);
  color: #063c6d;
  font-weight: 500;
  padding: 10px 12px;
  transition: background-color 0.12s ease;
}

.releases-table ::v-deep(.vgt-table.modern-table tr:last-child td) {
  border-bottom: none;
}

.releases-table ::v-deep(.vgt-table.modern-table tr:hover td) {
  background-color: rgba(6, 60, 109, 0.045);
}

.map-card {
  min-height: 0;
  display: flex;
  flex-direction: column;
  background: #ffffff;
  border: 1px solid rgba(6, 60, 109, 0.08);
  border-radius: 14px;
  box-shadow: 0 2px 12px rgba(6, 60, 109, 0.08);
  overflow: hidden;
}

.map-card-header {
  flex: 0 0 auto;
  padding: 0.7rem 1.1rem;
  background: linear-gradient(135deg, #ffffff 0%, #f6fafd 100%);
  border-bottom: 1px solid rgba(6, 60, 109, 0.08);
}

.map-card-title {
  display: flex;
  align-items: center;
  gap: 0.6rem;
}

.map-card-title-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 30px;
  height: 30px;
  border-radius: 9px;
  background-color: rgba(6, 60, 109, 0.1);
  color: #063c6d;
  font-size: 0.85rem;
}

.map-card-title-text {
  color: #063c6d;
  font-weight: 700;
  font-size: 1rem;
  letter-spacing: 0.2px;
}

.map-header-btn {
  background-color: #063c6d;
  color: white;
  border-radius: 6px;
  white-space: nowrap;
}

.map-header-btn:hover {
  background-color: #0a548f;
  color: white;
}

/* Fullscreen toggle button */
.map-fullscreen-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border: none;
  border-radius: 9px;
  background-color: rgba(6, 60, 109, 0.08);
  color: #063c6d;
  font-size: 0.85rem;
  cursor: pointer;
  transition: background-color 0.15s ease, color 0.15s ease, transform 0.15s ease;
}

.map-fullscreen-btn:hover {
  background-color: #063c6d;
  color: #ffffff;
  transform: translateY(-1px);
}

/* Maximized: fill the dashboard content area while keeping the side menu
   and the top welcome nav visible (does not cover the whole viewport) */
.map-card--fullscreen {
  position: absolute;
  inset: 12px 12px 12px 12px;
  z-index: 1500;
  width: auto !important;
  height: auto !important;
}

.map-body {
  position: relative;
  flex: 1 1 auto;
  min-height: 0;
}

#mmp-dashboard-map {
  height: 100%;
  width: 100%;
}

.map-layer-panel {
  position: absolute;
  bottom: 16px;
  left: 16px;
  z-index: 5;
  width: 258px;
  max-height: 62%;
  display: flex;
  flex-direction: column;
  background-color: rgba(255, 255, 255, 0.82);
  backdrop-filter: blur(14px) saturate(160%);
  -webkit-backdrop-filter: blur(14px) saturate(160%);
  border: 1px solid rgba(255, 255, 255, 0.6);
  border-radius: 14px;
  box-shadow: 0 8px 28px rgba(6, 60, 109, 0.18), 0 1px 2px rgba(6, 60, 109, 0.1);
  padding: 0.75rem 0.85rem;
  transition: max-height 0.2s ease, padding 0.2s ease;
}

.map-layer-panel--minimized {
  max-height: none;
  padding-bottom: 0.75rem;
}

.map-layer-panel-header {
  flex: 0 0 auto;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.5rem;
}

.map-layer-panel:not(.map-layer-panel--minimized) .map-layer-panel-header {
  margin-bottom: 0.55rem;
  padding-bottom: 0.5rem;
  border-bottom: 1px solid rgba(6, 60, 109, 0.1);
}

.map-layer-panel-title {
  display: flex;
  align-items: center;
  gap: 0.4rem;
  font-size: 0.82rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.6px;
  color: #063c6d;
  margin: 0;
}

.map-layer-panel-title::before {
  content: '';
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background-color: #1f9d55;
  box-shadow: 0 0 0 3px rgba(31, 157, 85, 0.18);
}

.map-layer-panel-minimize {
  flex: 0 0 auto;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  padding: 0;
  border: none;
  background-color: rgba(6, 60, 109, 0.06);
  color: #063c6d;
  border-radius: 7px;
  cursor: pointer;
  font-size: 0.72rem;
  transition: background-color 0.15s ease, color 0.15s ease;
}

.map-layer-panel-minimize:hover {
  background-color: rgba(6, 60, 109, 0.14);
}

.layer-toggle-list {
  display: flex;
  flex-direction: column;
  gap: 4px;
  flex: 1 1 auto;
  min-height: 0;
  max-height: 220px;
  overflow-y: scroll;
  overflow-x: hidden;
}

.layer-toggle-list::-webkit-scrollbar {
  width: 5px;
  background-color: transparent !important;
}
.layer-toggle-list::-webkit-scrollbar-track {
  background-color: transparent !important;
  box-shadow: none !important;
}
.layer-toggle-list::-webkit-scrollbar-thumb {
  background-color: transparent !important;
  border-radius: 999px;
}
.map-layer-panel:hover .layer-toggle-list::-webkit-scrollbar-track {
  background-color: rgba(6, 60, 109, 0.06) !important;
  border-radius: 999px;
}
.map-layer-panel:hover .layer-toggle-list::-webkit-scrollbar-thumb {
  background-color: rgba(6, 60, 109, 0.25) !important;
}
.map-layer-panel:hover .layer-toggle-list::-webkit-scrollbar-thumb:hover {
  background-color: rgba(6, 60, 109, 0.5) !important;
}

.layer-row {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 6px 8px;
  border-radius: 8px;
  transition: background-color 0.15s ease, box-shadow 0.15s ease;
}

.layer-row:hover {
  background-color: rgba(255, 255, 255, 0.7);
  box-shadow: 0 1px 4px rgba(6, 60, 109, 0.08);
}

.layer-drag-handle {
  flex: 0 0 auto;
  color: #9fb3c8;
  cursor: grab;
  padding: 2px;
}

.layer-drag-handle:hover {
  color: #063c6d;
}

.layer-row:active .layer-drag-handle {
  cursor: grabbing;
}

.layer-row-label {
  flex: 1 1 auto;
  min-width: 0;
  overflow-wrap: anywhere;
  word-break: break-word;
  line-height: 1.25;
  color: #063c6d;
  font-size: 0.9rem;
}

.layer-toggle-switch {
  position: relative;
  flex: 0 0 auto;
  width: 38px;
  height: 20px;
  border: none;
  border-radius: 999px;
  background-color: #cbd5e1;
  cursor: pointer;
  padding: 0;
  transition: background-color 0.2s ease;
}

.layer-toggle-switch.is-on {
  background-color: #1f9d55;
  box-shadow: 0 0 0 3px rgba(31, 157, 85, 0.15);
}

.layer-toggle-knob {
  position: absolute;
  top: 2px;
  left: 2px;
  width: 16px;
  height: 16px;
  border-radius: 50%;
  background-color: white;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.3);
  transition: left 0.2s cubic-bezier(0.4, 0, 0.2, 1);
}

.layer-toggle-switch.is-on .layer-toggle-knob {
  left: 20px;
}

.mmp-copyright {
  position: fixed;
  bottom: 10px;
  right: 12px;
  z-index: 5;
}

.mmp-attribution {
  display: inline-flex;
  align-items: center;
  padding: 5px 14px;
  color: #063c6d !important;
  font-size: 13px;
  font-weight: 600;
  letter-spacing: 0.01em;
  text-decoration: none !important;
  background: rgba(255, 255, 255, 0.55);
  backdrop-filter: blur(8px);
  border: none;
  border-radius: 999px;
  transition: color 0.15s ease, background 0.15s ease;
}

.mmp-attribution:hover {
  color: #063c6d !important;
  background: rgba(255, 255, 255, 0.8);
  text-decoration: none !important;
}
</style>
